import { differenceInDays, format, parseISO, isValid } from 'date-fns';

export const getExpiryStatus = (dateStr) => {
  if (!dateStr) return { status: 'missing', label: 'Missing', color: 'gray', days: null };
  const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
  if (!isValid(date)) return { status: 'missing', label: 'Missing', color: 'gray', days: null };
  const days = differenceInDays(date, new Date());
  if (days < 0) return { status: 'expired', label: 'Expired', color: 'red', days };
  if (days <= 7) return { status: 'critical', label: `${days}d left`, color: 'red', days };
  if (days <= 30) return { status: 'warning', label: `${days}d left`, color: 'orange', days };
  if (days <= 60) return { status: 'soon', label: `${days}d left`, color: 'yellow', days };
  return { status: 'valid', label: 'Valid', color: 'green', days };
};

export const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    return format(date, 'dd MMM yyyy');
  } catch { return '—'; }
};

export const formatCurrency = (amount, currency = '€') =>
  amount != null ? `${currency}${Number(amount).toLocaleString('en-EU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

export const today = () => format(new Date(), 'yyyy-MM-dd');

// ── Mileage ───────────────────────────────────────────────────────────────────
// Owned vehicles store mileage in KM (canonical). Comparables keep their source
// unit in `mileage_unit`, so convert per-record rather than assuming.
export const KM_PER_MILE = 1.60934;

export const kmToMiles = (km) => (km == null || km === '' ? null : Number(km) / KM_PER_MILE);
export const milesToKm = (mi) => (mi == null || mi === '' ? null : Number(mi) * KM_PER_MILE);

// "45,000 km · 27,962 mi" — pass both:false for just the primary unit.
export const formatMileage = (value, { unit = 'km', both = true } = {}) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!isFinite(n)) return '—';
  const isMiles = String(unit).toLowerCase().startsWith('mi');
  const primary = `${Math.round(n).toLocaleString()} ${isMiles ? 'mi' : 'km'}`;
  if (!both) return primary;
  const converted = isMiles ? n * KM_PER_MILE : n / KM_PER_MILE;
  return `${primary} · ${Math.round(converted).toLocaleString()} ${isMiles ? 'km' : 'mi'}`;
};

// The vehicle record and the Collector Score data each keep their own mileage (both in km).
// Returns null when they agree closely enough, otherwise says by how much and whether the gap
// looks like a km/miles mix-up (one value is ~1.61x or ~0.62x the other).
export const mileageMismatch = (a, b) => {
  const x = Number(a), y = Number(b);
  if (!(x > 0) || !(y > 0)) return null;           // one side not recorded: nothing to compare
  const diff = Math.abs(x - y);
  if (diff <= 1000 || diff / Math.max(x, y) <= 0.05) return null;
  const ratio = Math.max(x, y) / Math.min(x, y);
  return { a: x, b: y, unitMixup: Math.abs(ratio - KM_PER_MILE) / KM_PER_MILE < 0.04 };
};
